import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  HelpCircle,
  Inbox,
  Info,
  Link2,
  Loader2,
  Mail,
  PlugZap,
  Server,
  ShieldCheck,
  Trash2,
  Unplug,
  Users,
  XCircle,
} from "lucide-react";
import type {
  MailboxDefaults,
  MailboxInfo,
  MailboxTestResult,
  UnlinkedEmailRecord,
} from "@/types/email";
import { useConfirm } from "@/context/ConfirmContext";
import { dateLocale, withClock } from "@/lib/dateTime";

export type EmailTabKey = "connection" | "team" | "unlinked" | "guide";

interface ProviderPreset {
  id: string;
  name: string;
  desc: string;
  smtp_host: string;
  smtp_port: number;
  imap_host: string;
  imap_port: number;
}

const PROVIDERS: ProviderPreset[] = [
  {
    id: "hostinger",
    name: "Company webmail",
    desc: "The usual choice. Your webmail email and password.",
    smtp_host: "smtp.hostinger.com",
    smtp_port: 465,
    imap_host: "imap.hostinger.com",
    imap_port: 993,
  },
  {
    id: "google",
    name: "Gmail",
    desc: "Needs a 16-character app password.",
    smtp_host: "smtp.gmail.com",
    smtp_port: 465,
    imap_host: "imap.gmail.com",
    imap_port: 993,
  },
  {
    id: "microsoft",
    name: "Outlook",
    desc: "Work email account with mail apps allowed.",
    smtp_host: "smtp.office365.com",
    smtp_port: 587,
    imap_host: "outlook.office365.com",
    imap_port: 993,
  },
  {
    id: "custom",
    name: "Other mail service",
    desc: "Any other mail service. Enter its mail server details.",
    smtp_host: "",
    smtp_port: 465,
    imap_host: "",
    imap_port: 993,
  },
];

export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString(dateLocale(), withClock({
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit" }))
    : "Never";

/** Settings → Email: Connect personal/business mailbox, test credentials, and view team sync health */
export function EmailSettingsPage() {
  const { user, hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const canSeeTeam = hasPermission("mailbox.admin") || hasPermission("setting.manage");

  const requested = (params.get("tab") || "connection") as EmailTabKey;
  const tab: EmailTabKey = requested === "team" && !canSeeTeam ? "connection" : requested;
  const setTab = (key: EmailTabKey) => setParams(key === "connection" ? {} : { tab: key }, { replace: true });

  const [mailbox, setMailbox] = useState<MailboxInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMailbox = useCallback(async () => {
    try {
      const res = unwrapObject<{ mailbox: MailboxInfo | null }>(await api.get("/api/Mailbox/Me"));
      setMailbox(res?.mailbox || null);
    } catch {
      setMailbox(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  const tabs: { key: EmailTabKey; label: string; icon: React.ElementType; hint: string }[] = [
    { key: "connection", label: "My Connection", icon: PlugZap, hint: "Connect email to send quotes & sync deal replies" },
    ...(canSeeTeam
      ? [{ key: "team" as EmailTabKey, label: "Team Mailboxes", icon: Users, hint: "View team mailbox connection health & sync status" }]
      : []),
    { key: "unlinked", label: "Unmatched Deal Emails", icon: Inbox, hint: "Incoming customer emails needing deal assignment" },
    { key: "guide", label: "Setup Guide", icon: HelpCircle, hint: "Mail server details and app passwords" },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card border rounded-2xl p-5 shadow-xs">
        <div className="flex items-start sm:items-center gap-3.5">
          <div className="size-11 rounded-xl bg-gradient-to-br from-primary to-primary flex items-center justify-center text-white shadow-md shadow-primary/20 shrink-0">
            <Mail className="size-6" />
          </div>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Email Connection</h1>
              {loading ? (
                <Badge variant="outline" className="text-xs">Loading...</Badge>
              ) : mailbox?.sync_status === "CONNECTED" ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-success/15 text-success border border-success/30">
                  <span className="size-1.5 rounded-full bg-success animate-pulse" />
                  Connected ({mailbox.email_address})
                </span>
              ) : mailbox?.sync_status === "AUTH_FAILED" ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-destructive/15 text-destructive border border-destructive/30">
                  <span className="size-1.5 rounded-full bg-destructive" />
                  Authentication Failed
                </span>
              ) : mailbox ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-warning/15 text-warning border border-warning/30">
                  <span className="size-1.5 rounded-full bg-warning" />
                  Sync Issue
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
                  Not Connected
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Connect your company mailbox to send quotation PDFs, customer email replies, and automatically track communications on your deals.
            </p>
          </div>
        </div>

        {mailbox && (
          <div className="text-left sm:text-right shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0">
            <span className="text-[11px] text-muted-foreground block">Last Sync Check</span>
            <span className="text-xs font-medium text-foreground">{formatDateTime(mailbox.last_synced_at)}</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b overflow-x-auto" role="tablist">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isSelected = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors cursor-pointer",
                isSelected
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="size-4 shrink-0" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === "connection" && (
        <ConnectionTab
          mailbox={mailbox}
          onUpdated={loadMailbox}
        />
      )}

      {tab === "team" && canSeeTeam && (
        <TeamMailboxesTab />
      )}

      {tab === "unlinked" && (
        <UnlinkedEmailsTab />
      )}

      {tab === "guide" && (
        <SetupGuideTab onSelectPreset={(p) => {
          setTab("connection");
        }} />
      )}
    </div>
  );
}

/** 1. Connection Tab: Provider selection, credentials form, live test, and sync options */
export function ConnectionTab({
  mailbox,
  onUpdated,
}: {
  mailbox: MailboxInfo | null;
  onUpdated: () => void;
}) {
  const confirm = useConfirm();
  const { user, hasPermission } = useAuth();
  const { success, error } = useToast();
  const canSend = hasPermission("email.send");

  const [selectedProvider, setSelectedProvider] = useState<string>("hostinger");
  const [form, setForm] = useState({
    email_address: mailbox?.email_address || user?.Email || "",
    password: "",
    display_name: mailbox?.display_name || `${user?.FirstName || ""} ${user?.LastName || ""}`.trim(),
    smtp_host: mailbox?.smtp_host || PROVIDERS[0]!.smtp_host,
    smtp_port: mailbox?.smtp_port || PROVIDERS[0]!.smtp_port,
    imap_host: mailbox?.imap_host || PROVIDERS[0]!.imap_host,
    imap_port: mailbox?.imap_port || PROVIDERS[0]!.imap_port,
    sync_enabled: mailbox ? mailbox.sync_enabled : true,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<MailboxTestResult | null>(null);

  // Sync form when mailbox updates
  useEffect(() => {
    if (mailbox) {
      setForm((f) => ({
        ...f,
        email_address: mailbox.email_address || "",
        display_name: mailbox.display_name || f.display_name,
        smtp_host: mailbox.smtp_host,
        smtp_port: mailbox.smtp_port,
        imap_host: mailbox.imap_host,
        imap_port: mailbox.imap_port,
        sync_enabled: mailbox.sync_enabled,
        password: "",
      }));

      // Detect matching provider
      const matched = PROVIDERS.find((p) => p.smtp_host === mailbox.smtp_host);
      if (matched) setSelectedProvider(matched.id);
      else setSelectedProvider("custom");
    }
  }, [mailbox]);

  const applyPreset = (provider: ProviderPreset) => {
    setSelectedProvider(provider.id);
    if (provider.id !== "custom") {
      setForm((f) => ({
        ...f,
        smtp_host: provider.smtp_host,
        smtp_port: provider.smtp_port,
        imap_host: provider.imap_host,
        imap_port: provider.imap_port,
      }));
    }
  };

  const payload = () => ({
    email_address: form.email_address.trim(),
    password: form.password || undefined,
    display_name: form.display_name.trim() || undefined,
    smtp_host: form.smtp_host.trim(),
    smtp_port: Number(form.smtp_port),
    imap_host: form.imap_host.trim(),
    imap_port: Number(form.imap_port),
    sync_enabled: form.sync_enabled,
  });

  const handleTest = async () => {
    if (!form.email_address.trim()) {
      error("Missing email", "Please enter your email address to test the connection.");
      return;
    }
    if (!mailbox && !form.password) {
      error("Missing password", "Please enter your mailbox password to test.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = unwrapObject<MailboxTestResult>(await api.post("/api/Mailbox/Test", payload()));
      setTestResult(res);
      if (res.smtp_ok && res.imap_ok) {
        success("Test Passed", "Sending and receiving both work.");
      } else {
        error("Test Warning", "Sending or receiving didn't work. Check the email and password.");
      }
    } catch (err: any) {
      error("Test failed", err.message || "Couldn't reach the mail server");
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/Mailbox/Connect", payload());
      success("Mailbox Connected", `Your emails will now be sent from ${form.email_address.trim()}`);
      setTestResult(null);
      onUpdated();
    } catch (err: any) {
      error("Connection failed", err.message || "The mail server didn't accept the email and password");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!(await confirm({ title: "Disconnect this mailbox?", message: "Past emails linked to deals will remain safe.", confirmLabel: "Disconnect", destructive: true }))) {
      return;
    }
    try {
      await api.delete("/api/Mailbox/Disconnect");
      success("Disconnected", "Your mailbox has been unlinked successfully.");
      setTestResult(null);
      onUpdated();
    } catch (err: any) {
      error("Disconnect failed", err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Provider Quick Presets */}
      <div className="bg-card border rounded-2xl p-5 shadow-xs space-y-3">
        <div>
          <h2 className="text-sm font-bold text-foreground">Select Email Provider</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Choose your email service and we'll fill in the mail server details for you.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
          {PROVIDERS.map((provider) => {
            const isSelected = selectedProvider === provider.id;
            return (
              <button
                key={provider.id}
                type="button"
                onClick={() => applyPreset(provider)}
                className={cn(
                  "flex flex-col text-left p-3.5 rounded-xl border transition-all cursor-pointer relative group",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-xs"
                    : "border-border/80 bg-background hover:bg-muted/40 hover:border-border"
                )}
              >
                <div className="flex items-center justify-between w-full mb-1">
                  <span className="font-semibold text-xs text-foreground flex items-center gap-1.5">
                    <Server className={cn("size-3.5", isSelected ? "text-primary" : "text-muted-foreground")} />
                    {provider.name}
                  </span>
                  {isSelected && (
                    <span className="size-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px]">
                      ✓
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {provider.desc}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Connection Form */}
      {canSend ? (
        <form onSubmit={handleConnect} className="bg-card border rounded-2xl p-5 shadow-xs space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-4">
            <div>
              <h2 className="text-sm font-bold text-foreground">
                {mailbox ? "Update Mailbox Connection" : "Mailbox Login"}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Passwords are stored safely and never shown again.
              </p>
            </div>

            {mailbox && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                className="text-destructive hover:bg-destructive/10 border-destructive/30 self-start sm:self-auto gap-1.5 text-xs"
              >
                <Unplug className="size-3.5" />
                Disconnect Mailbox
              </Button>
            )}
          </div>

          {/* Credentials Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Business Email Address *</Label>
              <Input
                type="email"
                required
                value={form.email_address}
                onChange={(e) => setForm((f) => ({ ...f, email_address: e.target.value }))}
                placeholder="sales@nelgates.com"
                className="h-9 text-xs sm:text-sm"
              />
              <span className="text-[11px] text-muted-foreground">The email account used to send quotation emails.</span>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Mailbox Password {mailbox ? "(leave blank to keep current)" : "*"}
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                required={!mailbox}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={mailbox ? "•••••••• (Password saved)" : "Enter email password or app password"}
                className="h-9 text-xs sm:text-sm"
              />
              <span className="text-[11px] text-muted-foreground">
                For Gmail, use a 16-character app password.
              </span>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Display Name (Shown to Customers)</Label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                placeholder="Nelgates Sales Team"
                className="h-9 text-xs sm:text-sm"
              />
              <span className="text-[11px] text-muted-foreground">Appears in customer inbox as From: Name.</span>
            </div>

            <div className="flex items-center justify-between gap-3 p-3 rounded-xl border bg-muted/20">
              <div className="space-y-0.5">
                <span className="text-xs font-medium text-foreground block">Sync Customer Replies</span>
                <span className="text-[11px] text-muted-foreground block">
                  Automatically match customer reply emails to deal activity history.
                </span>
              </div>
              <Switch
                checked={form.sync_enabled}
                onCheckedChange={(val) => setForm((f) => ({ ...f, sync_enabled: val }))}
              />
            </div>
          </div>

          {/* Advanced Server Settings Toggle */}
          <div className="pt-2 border-t">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline cursor-pointer"
            >
              <ChevronDown className={cn("size-3.5 transition-transform", showAdvanced && "rotate-180")} />
              {showAdvanced ? "Hide Mail Server Details" : "Change Mail Server Details"}
            </button>

            {showAdvanced && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 p-3.5 rounded-xl bg-muted/30 border border-border/80">
                <div className="space-y-1">
                  <Label className="text-[11px]">Sending mail server</Label>
                  <Input
                    value={form.smtp_host}
                    onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                    placeholder="smtp.yourcompany.com"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Sending port number</Label>
                  <Input
                    type="number"
                    value={form.smtp_port}
                    onChange={(e) => setForm((f) => ({ ...f, smtp_port: Number(e.target.value) }))}
                    placeholder="465"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Receiving mail server</Label>
                  <Input
                    value={form.imap_host}
                    onChange={(e) => setForm((f) => ({ ...f, imap_host: e.target.value }))}
                    placeholder="imap.yourcompany.com"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Receiving port number</Label>
                  <Input
                    type="number"
                    value={form.imap_port}
                    onChange={(e) => setForm((f) => ({ ...f, imap_port: Number(e.target.value) }))}
                    placeholder="993"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Test Connection Results Card */}
          {testResult && (
            <div className="p-3.5 rounded-xl border bg-card space-y-1.5 text-xs">
              <span className="font-semibold text-foreground block mb-1">Connection Test Report:</span>
              <div className="flex items-center gap-2">
                {testResult.smtp_ok ? (
                  <CheckCircle2 className="size-4 text-success shrink-0" />
                ) : (
                  <XCircle className="size-4 text-destructive shrink-0" />
                )}
                <span className={testResult.smtp_ok ? "text-success" : "text-destructive"}>
                  <strong>Sending:</strong> {testResult.smtp_ok ? "Ready to send" : testResult.smtp_error}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {testResult.imap_ok ? (
                  <CheckCircle2 className="size-4 text-success shrink-0" />
                ) : (
                  <XCircle className="size-4 text-destructive shrink-0" />
                )}
                <span className={testResult.imap_ok ? "text-success" : "text-destructive"}>
                  <strong>Receiving:</strong> {testResult.imap_ok ? "Connected, replies will be picked up" : testResult.imap_error}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              disabled={testing || saving}
              onClick={handleTest}
              className="gap-2 text-xs h-9 cursor-pointer"
            >
              {testing ? <Loader2 className="size-3.5 animate-spin" /> : <ShieldCheck className="size-3.5" />}
              Test Connection
            </Button>
            <Button
              type="submit"
              disabled={saving || testing}
              className="gap-2 text-xs h-9 cursor-pointer"
            >
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <PlugZap className="size-3.5" />}
              {mailbox ? "Save & Reconnect" : "Connect Mailbox"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="bg-card border rounded-2xl p-6 text-center space-y-2">
          <Mail className="size-8 mx-auto text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">Missing email permissions</p>
          <p className="text-xs text-muted-foreground">You need the "email.send" permission to connect a mailbox.</p>
        </div>
      )}
    </div>
  );
}

/** 2. Team Mailboxes Tab: Overview of all sales reps across the organization */
export function TeamMailboxesTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/api/Mailbox/All")
      .then((res) => setRows(unwrapArray(res)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b bg-muted/10">
        <div className="flex items-center gap-2">
          <Users className="size-4 text-primary" />
          <h2 className="text-sm font-bold text-foreground">Team Mailbox Status</h2>
        </div>
        <span className="text-xs text-muted-foreground font-medium">
          {rows.length} total sales reps
        </span>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-xs text-muted-foreground">
          No team members found in your data scope.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                <th className="py-2.5 px-4 font-semibold">User</th>
                <th className="py-2.5 px-4 font-semibold">Connected Mailbox</th>
                <th className="py-2.5 px-4 font-semibold">Status</th>
                <th className="py-2.5 px-4 font-semibold">Reply Sync</th>
                <th className="py-2.5 px-4 font-semibold">Last Checked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {rows.map((row) => (
                <tr key={row.user_id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">
                    {row.user_name}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground font-mono text-[11px]">
                    {row.email_address || <span className="text-muted-foreground/50">None</span>}
                  </td>
                  <td className="py-3 px-4">
                    {row.sync_status === "CONNECTED" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/15 text-success">
                        <span className="size-1.5 rounded-full bg-success" />
                        Connected
                      </span>
                    ) : row.sync_status === "AUTH_FAILED" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-destructive/15 text-destructive">
                        Auth Failed
                      </span>
                    ) : row.email_address ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/15 text-warning">
                        Problem
                      </span>
                    ) : (
                      <span className="text-muted-foreground/60 text-[11px]">Not Connected</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {row.sync_enabled ? (
                      <span className="text-success font-medium">Enabled</span>
                    ) : (
                      <span className="text-muted-foreground">Disabled</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground text-[11px]">
                    {formatDateTime(row.last_synced_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** 3. Unlinked Deal Emails Tab */
function UnlinkedEmailsTab() {
  const { success, error } = useToast();
  const [emails, setEmails] = useState<UnlinkedEmailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/api/DealEmail/Unlinked?page=${page}`)
      .then((res) => {
        const paged = unwrapPage<UnlinkedEmailRecord>(res);
        setEmails(paged.rows);
        setTotal(paged.total);
        setPageSize(paged.page_size);
      })
      .catch(() => setEmails([]))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(load, [load]);

  const link = async (email: UnlinkedEmailRecord) => {
    const dealId = choice[email.id];
    if (!dealId) return;
    setBusyId(email.id);
    try {
      await api.post(`/api/DealEmail/Link/${email.id}`, { deal_id: dealId });
      success("Email linked", "The email thread is now attached to the deal.");
      load();
    } catch (err: any) {
      error("Could not link", err.message);
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (email: UnlinkedEmailRecord) => {
    setBusyId(email.id);
    try {
      await api.delete(`/api/DealEmail/Dismiss/${email.id}`);
      load();
    } catch (err: any) {
      error("Could not dismiss", err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b bg-muted/10">
        <div className="flex items-center gap-2">
          <Inbox className="size-4 text-primary" />
          <h2 className="text-sm font-bold text-foreground">Unmatched Inbound Emails</h2>
        </div>
        {total > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning/15 text-warning font-semibold">
            {total} pending
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : emails.length === 0 ? (
        <div className="p-8 text-center text-xs text-muted-foreground space-y-1">
          <CheckCircle2 className="size-7 mx-auto text-success mb-2" />
          <p className="font-semibold text-foreground">All emails linked to deals</p>
          <p>When incoming emails cannot be automatically matched to a single deal, they appear here.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border/60">
          {emails.map((e) => (
            <li key={e.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-muted/30 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-foreground truncate">{e.subject || "(No Subject)"}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  {e.direction === "IN" ? `From ${e.from_name || e.from_address}` : `To ${e.to_addresses.join(", ")}`}
                  {e.account_name && ` · ${e.account_name}`} · {formatDateTime(e.email_date)}
                </div>
                {e.snippet && (
                  <p className="text-[11px] text-muted-foreground/80 line-clamp-1 mt-1 bg-muted/40 p-1.5 rounded">
                    {e.snippet}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {e.candidate_deals.length > 0 ? (
                  <>
                    <Select value={choice[e.id] || ""} onValueChange={(val) => setChoice((c) => ({ ...c, [e.id]: val }))}>
                      <SelectTrigger className="h-8 w-44 text-xs">
                        <SelectValue placeholder="Select Deal..." />
                      </SelectTrigger>
                      <SelectContent>
                        {e.candidate_deals.map((d) => (
                          <SelectItem key={d.id} value={d.id} className="text-xs">
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 gap-1.5 text-xs" disabled={!choice[e.id] || busyId === e.id} onClick={() => link(e)}>
                      <Link2 className="size-3.5" />
                      Link
                    </Button>
                  </>
                ) : (
                  <span className="text-[11px] text-muted-foreground">No candidate deal</span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="size-8 p-0 text-muted-foreground hover:text-destructive cursor-pointer"
                  title="Dismiss from CRM"
                  disabled={busyId === e.id}
                  onClick={() => dismiss(e)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ListPager page={page} pageSize={pageSize} total={total} onPage={setPage} disabled={loading} />
    </div>
  );
}

/** 4. Setup Guide Tab */
export function SetupGuideTab({ onSelectPreset }: { onSelectPreset: (id: string) => void }) {
  return (
    <div className="space-y-4">
      <div className="bg-card border rounded-2xl p-5 shadow-xs space-y-4">
        <div>
          <h2 className="text-sm font-bold text-foreground">Email Setup Guides & Best Practices</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Step-by-step help for connecting company webmail, Gmail or Outlook.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          {/* Hostinger Guide */}
          <div className="border rounded-xl p-4 bg-muted/15 space-y-2.5 flex flex-col">
            <div className="flex items-center gap-2">
              <span className="size-6 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center text-xs">H</span>
              <h3 className="text-xs font-bold text-foreground">Company webmail</h3>
            </div>
            <ol className="text-[11px] text-muted-foreground space-y-1.5 list-decimal list-inside flex-1">
              <li>Use your full business email address (e.g. <code>you@nelgates.com</code>).</li>
              <li>Use the exact password you use to log in to your webmail.</li>
              <li>Pick "Company webmail" above. The mail server details are filled in for you.</li>
            </ol>
            <Button variant="outline" size="sm" onClick={() => onSelectPreset("hostinger")} className="text-xs mt-2 w-full">
              Use Company Webmail
            </Button>
          </div>

          {/* Google Workspace Guide */}
          <div className="border rounded-xl p-4 bg-muted/15 space-y-2.5 flex flex-col">
            <div className="flex items-center gap-2">
              <span className="size-6 rounded-lg bg-danger/10 text-danger font-bold flex items-center justify-center text-xs">G</span>
              <h3 className="text-xs font-bold text-foreground">Gmail</h3>
            </div>
            <ol className="text-[11px] text-muted-foreground space-y-1.5 list-decimal list-inside flex-1">
              <li>Turn on 2-Step Verification in your email account settings.</li>
              <li>Go to Security → App passwords.</li>
              <li>Create a new app password (name it "Nelgates CRM").</li>
              <li>Paste the 16-character password into the password field.</li>
            </ol>
            <Button variant="outline" size="sm" onClick={() => onSelectPreset("google")} className="text-xs mt-2 w-full">
              Use Gmail
            </Button>
          </div>

          {/* Microsoft 365 Guide */}
          <div className="border rounded-xl p-4 bg-muted/15 space-y-2.5 flex flex-col">
            <div className="flex items-center gap-2">
              <span className="size-6 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center text-xs">M</span>
              <h3 className="text-xs font-bold text-foreground">Outlook</h3>
            </div>
            <ol className="text-[11px] text-muted-foreground space-y-1.5 list-decimal list-inside flex-1">
              <li>Ask your email admin to allow sending from mail apps for your account.</li>
              <li>Pick "Outlook" above. The mail server details are filled in for you.</li>
              <li>Use your work email and its app password.</li>
            </ol>
            <Button variant="outline" size="sm" onClick={() => onSelectPreset("microsoft")} className="text-xs mt-2 w-full">
              Use Outlook
            </Button>
          </div>
        </div>

        {/* Port Reference Table */}
        <div className="mt-4 pt-4 border-t space-y-2">
          <span className="text-xs font-bold text-foreground block">Port numbers, if you set it up yourself:</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-lg border bg-background space-y-1">
              <div className="font-semibold text-foreground">Sending</div>
              <div className="text-[11px] text-muted-foreground">Port 465 or 587. Used to send quotes and client emails.</div>
            </div>
            <div className="p-3 rounded-lg border bg-background space-y-1">
              <div className="font-semibold text-foreground">Receiving</div>
              <div className="text-[11px] text-muted-foreground">Port 993. Used to check for client replies and attach them to deals automatically.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailSettingsPage;
